import { faCheck, faCopy } from "@fortawesome/pro-solid-svg-icons";
import { usePimsExportFormat } from "@hooks";
import { writeToClipboardWithPimsFormat } from "@src/utils/clipboard";
import mixpanel from "mixpanel-browser";
import { useCallback, useEffect, useRef, useState } from "react";

import { DSCircularButton } from "../molecules";

export interface DSCopyButtonProps {
  /** Text to copy to clipboard */
  text: string;
  /** Test ID for automated testing */
  dataTestId: string;
}

/**
 * Copy button that copies text to clipboard with visual feedback.
 * Shows a checkmark icon for 1.5 seconds after successful copy.
 */
export const DSCopyButton = ({
  text,
  dataTestId,
}: DSCopyButtonProps) => {
  const pimsFormat = usePimsExportFormat();
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(false);
  const copiedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const errorTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const mountedRef = useRef(true);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
      if (copiedTimerRef.current)
        clearTimeout(copiedTimerRef.current);
      if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
    };
  }, []);

  const handleCopy = useCallback(async () => {
    if (!text) return;
    try {
      await writeToClipboardWithPimsFormat(text, pimsFormat);
      setCopied(true);
      setError(false);
      mixpanel.track("Copied Note", {
        copyType: "copy_button",
        pimsExportFormat: pimsFormat,
      });
      if (copiedTimerRef.current)
        clearTimeout(copiedTimerRef.current);
      copiedTimerRef.current = setTimeout(() => {
        if (mountedRef.current) setCopied(false);
      }, 1500);
    } catch (err) {
      console.error("Failed to copy to clipboard:", err);
      setError(true);
      if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
      errorTimerRef.current = setTimeout(() => {
        if (mountedRef.current) setError(false);
      }, 1500);
    }
  }, [text, pimsFormat]);

  const ariaLabel = error
    ? "Failed to copy"
    : copied
      ? "Copied to clipboard"
      : "Copy text to clipboard";

  return (
    <DSCircularButton
      icon={copied ? faCheck : faCopy}
      size="sm"
      onClick={handleCopy}
      title={
        error ? "Failed to copy" : copied ? "Copied!" : "Copy text"
      }
      aria-label={ariaLabel}
      dataTestId={dataTestId}
    />
  );
};

DSCopyButton.displayName = "DSCopyButton";
